// Utility to create an element with attributes and children
function createElement(tag, { id, className, attrs = {}, children = [], events = {} } = {}) {
  const el = document.createElement(tag);
  if (id) el.id = id;
  if (className) el.className = className;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  Object.entries(events).forEach(([event, handler]) => el.addEventListener(event, handler));
  children.forEach(child => el.appendChild(child));
  return el;
}

// Utility to remove an element by id if it exists
function removeElementById(id) {
  document.getElementById(id)?.remove();
}

const pdpSectionCache = {};

function openPdpSectionPopup(productHandle, buttonType) {
  removeElementById('pdp-section-popup');

  // Create overlay
  const closeBtn = createElement('button', {
    children: [document.createTextNode('×')],
    attrs: { type: 'button', 'aria-label': 'Close popup' },
    className: 'pdp-section-popup__close',
    events: { click: () => removeElementById('pdp-section-popup') }
  });

  const spinner = createElement('div', {
    className: 'pdp-section-popup__spinner',
    children: [document.createTextNode('Loading...')]
  });

  const content = createElement('div', {
    className: 'pdp-section-popup__content',
    children: [closeBtn, spinner],
    events: { click: (e) => e.stopPropagation() }
  });

  const overlay = createElement('div', {
    id: 'pdp-section-popup',
    className: 'pdp-section-popup__overlay',
    children: [content],
    events: { click: () => removeElementById('pdp-section-popup') }
  });

  document.body.appendChild(overlay);

  
  // Check cache first
  if (pdpSectionCache[productHandle]) {
    const sectionHtml = pdpSectionCache[productHandle];
    content.innerHTML = '';
    content.appendChild(closeBtn);
    content.insertAdjacentHTML('beforeend', sectionHtml);
    return;
  }

  fetch(`/products/${productHandle}`)
  .then(r => r.ok ? r.text() : Promise.reject(r))
  .then(html => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    let sectionHtml = temp.querySelector('.product-information');
    if (!sectionHtml) sectionHtml = temp;
    sectionHtml.classList.add(buttonType);
    content.innerHTML = '';
    content.appendChild(closeBtn);
    content.insertAdjacentHTML('beforeend', sectionHtml.outerHTML);
    // Cache the section HTML
    pdpSectionCache[productHandle] = sectionHtml.outerHTML;
  })
  .catch(() => {
    spinner.textContent = 'Failed to load product section.';
  });
}

// Example usage: openPdpSectionPopup('my-product-handle', 'quick_view / quick_add');
// Quick view modal trigger for product cards
document.addEventListener('DOMContentLoaded', () => {
  document.body.addEventListener('click', async (e) => {
    if (!e.target) return;
    const btn = e.target.closest('.card_quick_view');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    openPdpSectionPopup(handle, btn.dataset.quickType);
  });
});


class PersistantDataElement extends HTMLElement {
  constructor() {
    super();
    this._syncHandler = this.update.bind(this);
  }
  connectedCallback() {
    this.update();
    this.addEventListener('persistant-data:sync', this._syncHandler);
  }
  disconnectedCallback() {
    this.removeEventListener('persistant-data:sync', this._syncHandler);
  }
  update() {
    const key = this.getAttribute('key');
    const forSelector = this.getAttribute('for');
    if (!key || !forSelector) return;
    const findCheckbox = () => this.querySelector(forSelector);
    const tryInit = () => {
      const checkbox = findCheckbox();
      if (!checkbox) {
        setTimeout(tryInit, 33);
        return;
      }
      const isChecked = localStorage.getItem(key);
      if (isChecked === 'true') {
        checkbox.checked = true;
      } else if (isChecked === 'false') {
        checkbox.checked = false;
      }
      checkbox.addEventListener('change', () => {
        localStorage.setItem(key, checkbox.checked);
      });
    };
    tryInit();
  }
}
customElements.define('persistant-data', PersistantDataElement);


document.addEventListener('DOMContentLoaded', () => {
  const headerRow = document.querySelector('.header__row.header__row--bottom');
  if (headerRow) {
    headerRow.addEventListener('mouseleave', () => {
      setTimeout(() => {
        headerRow.querySelectorAll('.menu-list__link[aria-expanded="true"]').forEach((link) => link.setAttribute('aria-expanded', 'false'));
      }, 300);
    })
  }
});

// Reusable reveal animation handler
function handleRevealOnScroll(elements, animationType) {
  const revealClasses = Array.from(document.body.getAttribute('data-animation-view-class').split(' ')) || [];
  const hiddenClasses = Array.from(document.body.getAttribute('data-animation-hidden-class').split(' ')) || [];
  const animationDelay = 50; // ms delay between each element's animation within the same section

  if (!elements.length) return;
  const header = document.querySelector('header');
  const headerHeight = header ? header.offsetHeight : 0;

  // Map each element to its parent .section and its index within that section
  const sectionMap = new Map();
  elements.forEach(el => {
    const section = el.closest('.section');
    if (section) {
      if (!sectionMap.has(section)) sectionMap.set(section, []);
      sectionMap.get(section).push(el);
    }
  });
  // For each section, sort its elements by DOM order
  sectionMap.forEach((els, section) => {
    els.sort((a, b) => a.compareDocumentPosition(b) & 2 ? 1 : -1);
  });

  const observer = new IntersectionObserver((entries) => {
    // Group entries by their parent .section
    const sectionEntries = new Map();
    entries.forEach(entry => {
      const section = entry.target.closest('.section');
      if (!section) return;
      if (!sectionEntries.has(section)) sectionEntries.set(section, []);
      sectionEntries.get(section).push(entry);
    });
    // For each section, animate its entries in DOM order
    sectionEntries.forEach((entryList, section) => {
      // Get the sorted animatable elements for this section
      const sectionEls = sectionMap.get(section) || [];
      // Sort entryList by DOM order within section
      entryList.sort((a, b) => sectionEls.indexOf(a.target) - sectionEls.indexOf(b.target));
      entryList.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add(...revealClasses);
            entry.target.classList.remove(...hiddenClasses);
          }, i * animationDelay);
        } else {
          if (animationType === 'once') return;
          entry.target.classList.remove(...revealClasses);
          entry.target.classList.add(...hiddenClasses);
        }
      });
    });
  }, {
    threshold: 0,
    rootMargin: `-${headerHeight}px 0px -80px 0px`
  });
  // On first load, skip animation for elements already in view
  elements.forEach(el => {
    const rect = el.getBoundingClientRect();
    const header = document.querySelector('header');
    const headerHeight = header ? header.offsetHeight : 0;
    const inView =
      rect.top < window.innerHeight &&
      rect.bottom > headerHeight;
    if (inView) {
      // Immediately show, skip animation
      el.classList.add('in_view');
      el.classList.remove('translate_down_10', 'opacity_0');
      el.classList.add('animate');
    } else {
      el.classList.add('animate', ...hiddenClasses);
    }
    observer.observe(el);
  });
  return observer;
}

document.addEventListener('DOMContentLoaded', () => {
  const animationType = document.body.getAttribute('data-animation-type');
  if (animationType === 'none') return;
  // Define animation groups and their exclusions
  const animationGroups = [
    {
      selector: '.product_card',
      excludeChildrenOf: [],
    },
    {
      selector: '.collection_card',
      excludeChildrenOf: [],
    },
    {
      selector: '.article_card',
      excludeChildrenOf: [],
    },
    {
      selector: '.text_block',
      excludeChildrenOf: ['.product_card', '.collection_card', '.article_card', '.menu-list__list-item', '.menu__details', '.email-signup-block', '.group-block'],
    },
    {
      selector: '.image_block',
      excludeChildrenOf: ['.product_card', '.collection_card', '.article_card'],
    },
    {
      selector: '.menu-list__list-item',
      excludeChildrenOf: [],
    },
    {
      selector: '.menu__details',
      excludeChildrenOf: [],
    },
    {
      selector: '.group-block',
      excludeChildrenOf: [],
    },
    {
      selector: '.email-signup-block',
      excludeChildrenOf: [],
    },
    {
      selector: '.social-icons__icon-wrapper',
      excludeChildrenOf: [],
    },
    {
      selector: 'a',
      excludeChildrenOf: ['.product_card', '.collection_card', '.article_card', '.menu-list__list-item', '.menu__details', '.email-signup-block', '.group-block'],
    },
  ];

  // Collect elements for animation, respecting exclusions and [data-animate="false"]
  const elementsToAnimate = [];
  animationGroups.forEach(group => {
    const candidates = document.querySelectorAll(group.selector);
    candidates.forEach(el => {
      // Exclude if inside any parent exclusion
      const isExcludedByParent = group.excludeChildrenOf.some(parentSel => el.closest(parentSel));
      // Exclude if inside [data-animate="false"]
      const isExcludedByData = el.closest('[data-animate="false"]');
      if (!isExcludedByParent && !isExcludedByData) {
        elementsToAnimate.push(el);
      }
    });
  });

  // Remove duplicates (in case of overlapping selectors)
  const uniqueElements = Array.from(new Set(elementsToAnimate));

  handleRevealOnScroll(uniqueElements, animationType);
});


document.addEventListener('DOMContentLoaded', handleEmptyElements);
document.addEventListener('shopify:section:load', handleEmptyElements);
document.addEventListener('shopify:section:reorder', handleEmptyElements);

function handleEmptyElements() {
  const tags = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span'];

  tags.forEach(tag => {
    document.querySelectorAll(tag).forEach(el => {
      const hasElementChildren = el.querySelector("*");
      if (hasElementChildren) return;

      if (!el.textContent.trim() || el.textContent.trim() === "/") {
        el.innerHTML = '';
      }
    });
  });
}

document.addEventListener("DOMContentLoaded", ()=>{
  const allPopupTriggers = document.querySelectorAll("[data-element-type='popup_trigger']");

  allPopupTriggers.forEach(trigger => {
    const content = document.querySelector(`[popup-content="${trigger.getAttribute("aria-controls")}"]`);
    const contentChild = content.children[0];
    if (!content) return;

    trigger.addEventListener("click", () => {
      content.classList.remove("hidden");
    })

    content.addEventListener("click", (e)=>{
      if (contentChild.contains(e.target) && !e.target.closest(`[aria-label="Close popup"]`)) {
        e.stopPropagation();
      } else {
        content.classList.add("hidden");
      }
    })
  })
})

class SlideToggle extends HTMLElement {
  connectedCallback() {
    const triggerKey = this.getAttribute("data-slide-trigger");
    const content = document.querySelector(`[data-slide-trigger-target="${triggerKey}"]`);

    if (!content) return;

    this.addEventListener("click", () => {
      content.setAttribute("aria-expanded", "true");
    });

    content.addEventListener("click", (e) => {
      e.stopPropagation();
      if (content.querySelector(".link_container")?.contains(e.target)) {
        if (!e.target.nodeName == "A") e.preventDefault();
      } else {
        content.setAttribute("aria-expanded", "false");
      }
    });
  }
}

customElements.define("slide-toggle", SlideToggle);

class ArticleFilters extends HTMLElement {
  connectedCallback() {
    this.buttons = this.querySelectorAll("button[data-value]");
    this.cards = this.nextElementSibling.querySelectorAll("[data-article-tag]");
    this.activeFilter = "all";

    this.buttons.forEach(button => {
      button.addEventListener("click", () => this.handleFilter(button));
    });
  }

  handleFilter(button) {
    this.activeFilter = button.getAttribute("data-value");
    console.log(this.activeFilter);

    // Update active button state
    this.buttons.forEach(btn => btn.removeAttribute("aria-selected"));
    button.setAttribute("aria-selected", "true");

    // Toggle cards
    console.log(this.parentElement.querySelectorAll("[data-article-tag]"))
    this.parentElement.querySelectorAll("[data-article-tag]").forEach(card => {
      const tag = card.getAttribute("data-article-tag");
      const visible = this.activeFilter === "all" || tag === this.activeFilter;
      card.hidden = !visible;
    });
  }
}

customElements.define("article-filters", ArticleFilters);